/**
 * What this browser is keeping for you, and how to take it with you.
 * ---------------------------------------------------------------------------
 * Everything someone sets here - the sizes, the palettes, the rail's order and
 * names, which renderer the stages use, how wide the findings column is - is
 * kept in this browser and nowhere else. Logging out clears it, so the last
 * thing before that should be the chance to keep it.
 *
 * What is NOT in the file: anything that gets you in. Tokens, keys and the
 * secret store's own material stay where they are - a settings file that is
 * also a credential is a settings file nobody can email to a colleague.
 * ---------------------------------------------------------------------------
 */

/* Keys that are credentials or sessions rather than preferences. Matched
   loosely on purpose: a new auth key appearing under a name nobody here
   predicted should be left out by default, not swept in. */
const SECRET = /(token|secret|password|credential|cognito|amplify|aws|idtoken|accesskey|session|jwt|auth)/i;

/* ...and a value that looks like a JWT whatever its key is called. */
const LOOKS_LIKE_JWT = /^ey[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\./;

export function isSettingKey(key, value) {
  if (!key || SECRET.test(key)) return false;
  if (typeof value === "string" && LOOKS_LIKE_JWT.test(value.trim())) return false;
  return true;
}

/** Everything this browser holds that is a preference rather than a key. */
export function collectLocalData() {
  const out = {};
  let skipped = 0;
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      const value = localStorage.getItem(key);
      if (isSettingKey(key, value)) out[key] = value; else skipped += 1;
    }
  } catch (e) { /* private window */ }
  return { data: out, kept: Object.keys(out).length, skipped };
}

/**
 * Write it out as a file.
 *
 * @returns how many entries went into it
 */
export function exportLocalData() {
  const { data, kept } = collectLocalData();
  const file = {
    app: "AppFactor",
    what: "Local settings for this browser",
    exportedAt: new Date().toISOString(),
    note: "Keys, tokens and session material are deliberately not included.",
    data,
  };

  const blob = new Blob([JSON.stringify(file, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `appfactor-settings-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  /* let the download start before the URL is taken back */
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return kept;
}

/**
 * Read one back in. Not wired to a control yet - it is here because an export
 * that cannot be imported is a backup of nothing, and the file format is
 * decided in one place or it is decided twice.
 */
export function importLocalData(file) {
  const entries = file && typeof file === "object" ? (file.data || {}) : {};
  let put = 0;
  Object.entries(entries).forEach(([key, value]) => {
    if (!isSettingKey(key, value) || typeof value !== "string") return;
    try { localStorage.setItem(key, value); put += 1; } catch (e) { /* private window */ }
  });
  return put;
}
